import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';


export const StripeContext = createContext({
  stripeStatus: null,
  isOnboarding: false,
  isStripeFullyConnected: () => false,
  fetchStripeStatus: () => {},
  getStripePromise: () => null,
});

// Stripe Context Provider
export const StripeProvider = ({ children }) => {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const stripePromiseRef = useRef(null);
  const keyPromiseRef = useRef(null);

  const apiurl = process.env.REACT_APP_API_URL;

  // Fetches the publishable key from the backend (with current token)
  const fetchKeyFromApi = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return Promise.resolve(null);
    return fetch(`${apiurl}/stripe/publishable-key`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Stripe key API error: ${res.status}`);
        return res.json();
      })
      .then((data) => data.publishable_key || null)
      .catch((err) => {
        console.error('Error fetching Stripe publishable key:', err);
        return null;
      });
  }, [apiurl]);

  // Returns a promise that resolves to a Stripe instance
  // Lazily fetches the key on first call (so it always has a fresh token)
  const getStripePromise = useCallback(async () => {
    if (stripePromiseRef.current) return stripePromiseRef.current;

    // Fetch key on demand if not already in flight
    if (!keyPromiseRef.current) {
      keyPromiseRef.current = fetchKeyFromApi();
    }
    const key = await keyPromiseRef.current;

    if (!key) {
      // Reset so next call retries (e.g. token was missing, now available)
      keyPromiseRef.current = null;
      console.error('Stripe publishable key not available');
      return null;
    }
    stripePromiseRef.current = loadStripe(key);
    return stripePromiseRef.current;
  }, [fetchKeyFromApi]);

  const fetchStripeStatus = useCallback(async (token) => {
    try {
      const response = await fetch(`${apiurl}/stripe/check-account`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Stripe API error: ${response.status}`);
      }

      const stripeData = await response.json();
      setStripeStatus(stripeData.status);

      if (stripeData.status === true) {
        const onboardingResponse = await fetch(`${apiurl}/stripe/get-account-status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (onboardingResponse.ok) {
          const onboardingData = await onboardingResponse.json();
          setIsOnboarding(onboardingData.payouts_enabled);
        } else {
          setIsOnboarding(false); // Fallback if onboarding status fetch fails
        }
      } else {
        setIsOnboarding(false); // No Stripe account, so no onboarding
      }
    } catch (err) {
      console.error('Error fetching Stripe status:', err);
      setStripeStatus(null);
      setIsOnboarding(false);
    }
  }, [apiurl]);

  // Utility function to check if Stripe is fully connected (account exists and onboarding complete)
  const isStripeFullyConnected = useCallback(() => {
    return stripeStatus === true && isOnboarding === true;
  }, [stripeStatus, isOnboarding]);

  const contextValue = React.useMemo(() => ({
    stripeStatus,
    isOnboarding,
    isStripeFullyConnected,
    fetchStripeStatus,
    getStripePromise,
  }), [stripeStatus, isOnboarding, isStripeFullyConnected, fetchStripeStatus, getStripePromise]);

  return (
    <StripeContext.Provider value={contextValue}>
      {children}
    </StripeContext.Provider>
  );
};